import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatSgd, apiErr } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input, Label } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Radar, RefreshCw, Landmark, Leaf, Link2, CircleCheck, CircleX, CircleHelp,
  ChevronDown, ChevronUp, AlertTriangle, SlidersHorizontal, Tag, ExternalLink, FilePlus2,
} from "lucide-react";

const TIER_TONE = {
  strong: "text-accent border-accent/50",
  likely: "text-[#00E65B] border-[#00E65B]/40",
  possible: "text-[#FFB800] border-[#FFB800]/40",
  weak: "text-muted-foreground border-border",
  known_failure: "text-red-400 border-red-400/40",
};
const CLASS_ICON = { green_loan_candidate: Leaf, sll_candidate: Link2, conventional_candidate: Landmark };
const INSTRUMENT_LABELS = {
  grant: "Grant", support_programme: "Support programme", government_risk_share_loan: "Govt risk-share loan",
  green_loan: "Green loan", sll: "Sustainability-linked loan", bank_green_loan: "Bank green loan",
};
const CRIT_ICON = { pass: [CircleCheck, "text-[#00E65B]"], fail: [CircleX, "text-red-400"], unknown: [CircleHelp, "text-[#FFB800]"] };

function WeightsDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const [w, setW] = useState(null);
  useEffect(() => {
    if (open) api.get("/susfin/funding-weights").then((r) => { setD(r.data); setW(r.data.weights); });
  }, [open]);
  const total = w ? Object.values(w).reduce((a, b) => a + Number(b || 0), 0) : 0;
  const save = async () => {
    try {
      await api.put("/susfin/funding-weights", Object.fromEntries(Object.entries(w).map(([k, v]) => [k, Number(v)])));
      toast.success("Scoring weights saved"); setOpen(false); onSaved?.();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="sf-fm-weights-btn">
        <SlidersHorizontal className="h-3.5 w-3.5" /> Weights
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="sf-fm-weights-dialog">
          <DialogHeader><DialogTitle className="font-display">Opportunity score weights</DialogTitle></DialogHeader>
          {d && w && (
            <div className="space-y-2">
              {Object.keys(d.defaults).map((k) => (
                <div key={k} className="flex items-center justify-between gap-3">
                  <Label className="text-xs flex-1">{d.labels[k]}</Label>
                  <Input type="number" className="h-8 w-20 text-xs" value={w[k]} onChange={(e) => setW((p) => ({ ...p, [k]: e.target.value }))} data-testid={`sf-fm-weight-${k}`} />
                </div>
              ))}
              <div className={`text-xs font-mono text-right ${Math.abs(total - 100) > 0.5 ? "text-red-400" : "text-muted-foreground"}`} data-testid="sf-fm-weights-total">Total: {total}%</div>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setW(d.defaults)}>Reset defaults</Button>
                <Button size="sm" variant="primary" onClick={save} disabled={Math.abs(total - 100) > 0.5} data-testid="sf-fm-weights-save">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function MatchCard({ c, iid, onChanged, onNavigate, onOpenFinancingCase, initiativeName }) {
  const [openCrit, setOpenCrit] = useState(false);
  const [appBusy, setAppBusy] = useState(false);
  const tagOffer = async () => {
    try {
      await api.post(`/initiatives/${iid}/tag-finance-offer`, { offer_id: c.offer_id });
      toast.success(`${c.name} tagged — you can now build a financing case`);
      onChanged?.();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const startGrantApplication = async () => {
    setAppBusy(true);
    try {
      const r = await api.post(`/susfin/register/${iid}/grant-application`, { grant_id: c.scheme_id });
      toast.success(r.data.created
        ? `${r.data.application.code} application created in the Grants tracker`
        : `Linked the existing open ${r.data.application.code} application`);
      onChanged?.();
      onNavigate?.("grants");
    } catch (e) { toast.error(apiErr(e)); } finally { setAppBusy(false); }
  };
  const createFinancingCase = async () => {
    setAppBusy(true);
    try {
      const r = await api.post("/green-finance/cases", {
        case_name: `${c.name} for ${initiativeName || "investment"}`,
        linked_offer_id: c.offer_id, linked_initiative_id: iid,
        financing_quantum_requested: Number(c.estimated_contribution_sgd || 0),
      });
      toast.success("Bank financing case created and linked to this investment");
      onOpenFinancingCase?.(r.data.id);
    } catch (e) { toast.error(apiErr(e)); } finally { setAppBusy(false); }
  };
  return (
    <div className={`card-flat p-4 ${c.tier === "known_failure" ? "opacity-60" : ""}`} data-testid={`sf-fm-card-${c.code || c.offer_id}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-display font-semibold">{c.name}</h4>
            <Badge variant="outline" className="text-[9px] uppercase">{INSTRUMENT_LABELS[c.instrument_type] || c.instrument_type}</Badge>
            {c.tagged && <Badge variant="outline" className="text-[9px] uppercase text-accent border-accent/50"><Tag className="h-2.5 w-2.5 mr-1" />Tagged</Badge>}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">{c.provider}{c.indicative_rate ? ` · indicative ${c.indicative_rate}` : ""}</div>
          <p className="text-xs text-muted-foreground mt-2">{c.eligibility_summary}</p>
          {c.commencement_restriction && (
            <p className="text-[11px] text-[#FFB800] mt-2 flex items-start gap-1" data-testid={`sf-fm-commencement-${c.code}`}>
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {c.commencement_note}
            </p>
          )}
          {c.missing_info?.length > 0 && (
            <div className="mt-2 text-[11px]" data-testid={`sf-fm-missing-${c.code || c.offer_id}`}>
              <span className="text-muted-foreground uppercase tracking-wider text-[9px]">Missing information · </span>
              <span className="text-[#FFB800]">{c.missing_info.join(" · ")}</span>
            </div>
          )}
          {c.criteria?.length > 0 && (
            <button onClick={() => setOpenCrit(!openCrit)} className="text-[11px] text-accent hover:underline mt-2 inline-flex items-center gap-1" data-testid={`sf-fm-crit-toggle-${c.code}`}>
              {openCrit ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />} Eligibility criteria ({c.criteria.length})
            </button>
          )}
          {openCrit && (
            <div className="mt-2 space-y-1">
              {c.criteria.map((r) => {
                const [Icon, cls] = CRIT_ICON[r.result] || CRIT_ICON.unknown;
                return (
                  <div key={r.key} className="flex items-start gap-2 text-[11px]">
                    <Icon className={`h-3.5 w-3.5 shrink-0 mt-0.5 ${cls}`} />
                    <span>{r.label}{r.result === "unknown" && r.verify_hint ? <span className="text-muted-foreground"> — {r.verify_hint}</span> : null}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="w-44 shrink-0 text-right">
          <div className="font-display text-3xl font-bold" data-testid={`sf-fm-confidence-${c.code || c.offer_id}`}>
            {c.confidence}<span className="text-sm text-muted-foreground">%</span>
          </div>
          <Badge variant="outline" className={`text-[9px] uppercase mt-1 ${TIER_TONE[c.tier]}`} data-testid={`sf-fm-tier-${c.code || c.offer_id}`}>{c.tier_label}</Badge>
          <div className="text-[10px] text-muted-foreground mt-2">Opportunity score <span className="font-mono text-foreground font-bold">{c.opportunity_score}</span>/100</div>
          {c.estimated_contribution_sgd != null && (
            <div className="mt-2">
              <div className="text-[9px] uppercase tracking-wider text-muted-foreground">Est. contribution</div>
              <div className="font-mono text-sm font-bold text-accent" data-testid={`sf-fm-contribution-${c.code || c.offer_id}`}>{formatSgd(c.estimated_contribution_sgd)}</div>
            </div>
          )}
          {c.deadline_note && <div className="text-[10px] text-muted-foreground mt-1">{c.deadline_note}</div>}
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-border/60 flex items-end justify-between flex-wrap gap-3">
        <div className="flex gap-2 flex-wrap flex-1 min-w-[280px]">
          {c.score_breakdown.map((b) => (
            <div key={b.key} className="w-[72px]" title={`${b.label}: ${b.score}/100 (weight ${b.weight}%)`}>
              <div className="h-1 bg-border rounded-full overflow-hidden"><div className="h-full bg-accent/70" style={{ width: `${b.score}%` }} /></div>
              <div className="text-[8px] uppercase tracking-wide text-muted-foreground mt-0.5 truncate">{b.label}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[9px] text-muted-foreground font-mono">rules v{c.rule_version}{c.last_verified ? ` · verified ${c.last_verified}` : ""}</span>
          {c.source === "scheme" && c.instrument_type !== "government_risk_share_loan" && (
            <Button size="sm" variant="outline" onClick={() => onNavigate?.("grants")} data-testid={`sf-fm-action-grants-${c.code}`}>
              Review in Grants <ExternalLink className="h-3 w-3" />
            </Button>
          )}
          {c.source === "scheme" && c.instrument_type === "government_risk_share_loan" && (
            <Button size="sm" variant="outline" onClick={() => onNavigate?.("financing")} data-testid={`sf-fm-action-financing-${c.code}`}>
              Open Bank Financing <ExternalLink className="h-3 w-3" />
            </Button>
          )}
          {c.source === "bank_offer" && !c.tagged && (
            <Button size="sm" variant="outline" onClick={tagOffer} data-testid={`sf-fm-action-tag-${c.offer_id}`}>
              <Tag className="h-3 w-3" /> Tag offer
            </Button>
          )}
          {c.tier !== "known_failure" && c.source === "scheme" && c.instrument_type !== "government_risk_share_loan" && (
            <Button size="sm" variant="primary" disabled={appBusy} onClick={startGrantApplication} data-testid={`sf-fm-action-apply-${c.code || c.offer_id}`}>
              <FilePlus2 className="h-3 w-3" /> Start grant application
            </Button>
          )}
          {c.tier !== "known_failure" && c.source === "bank_offer" && (
            <Button size="sm" variant="primary" disabled={appBusy} onClick={createFinancingCase} data-testid={`sf-fm-action-case-${c.offer_id}`}>
              <FilePlus2 className="h-3 w-3" /> Create financing case
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function FundingMatches({ id, onBack, onNavigate, onOpenFinancingCase }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/susfin/funding-match/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, [id]);

  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/susfin/funding-match/${id}/run`);
      setD(r.data);
      toast.success("Funding discovery complete");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!d) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading funding matches…</div>;
  const res = d.result;
  const cls = res?.classification || d.classification_preview;
  const ClsIcon = CLASS_ICON[cls?.primary?.value] || Landmark;

  return (
    <div className="space-y-5" data-testid="sf-funding-matches">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-1" data-testid="sf-fm-back">
            <ArrowLeft className="h-4 w-4" /> Investment Register
          </button>
          <h2 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2">
            <Radar className="h-5 w-5 text-accent" /> Funding Matches — {d.initiative_name}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <WeightsDialog onSaved={() => res && run()} />
          <Button variant="primary" onClick={run} disabled={busy} data-testid="sf-fm-run">
            <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> {res ? "Re-run discovery" : "Run funding discovery"}
          </Button>
        </div>
      </div>

      {cls && (
        <div className="card-flat p-5" data-testid="sf-fm-classification">
          <div className="flex items-center gap-3 flex-wrap">
            <ClsIcon className="h-5 w-5 text-accent" />
            <span className="font-display font-bold">{cls.primary.label}</span>
            {cls.alternatives?.map((a) => (
              <Badge key={a.value} variant="outline" className="text-[9px] uppercase">also: {a.label}</Badge>
            ))}
          </div>
          <ul className="text-xs text-muted-foreground mt-2 space-y-0.5">
            {cls.primary.reasons.map((r, idx) => <li key={idx}>· {r}</li>)}
          </ul>
          {cls.warnings?.map((w, idx) => (
            <p key={idx} className="text-[11px] text-[#FFB800] mt-2 flex items-start gap-1"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {w}</p>
          ))}
          <p className="text-[10px] text-muted-foreground mt-2">{cls.caveat}</p>
        </div>
      )}

      {!res ? (
        <div className="card-flat p-12 text-center text-muted-foreground" data-testid="sf-fm-empty">
          <Radar className="h-8 w-8 mx-auto mb-3 opacity-50" />
          Run funding discovery to screen this investment against all Singapore schemes and your bank offer catalogue.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="sf-fm-capital">
            {[["Total CAPEX", res.capital.total_capex], ["Best grant estimate", res.capital.best_grant_estimate],
              ["Company contribution", res.capital.company_contribution], ["Residual financing", res.capital.residual_financing]].map(([l, v]) => (
              <div key={l} className="card-flat p-3">
                <div className="label-eyebrow text-[10px] mb-1">{l}</div>
                <div className="font-mono text-lg font-bold">{formatSgd(v)}</div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span data-testid="sf-fm-meta">Run {new Date(res.run_at).toLocaleString()} · profile {res.profile_completeness}% complete · {res.matches.length} opportunities ranked</span>
          </div>

          <div className="space-y-3">
            {res.matches.map((c) => (
              <MatchCard key={c.code || c.offer_id} c={c} iid={id} onChanged={load} onNavigate={onNavigate}
                onOpenFinancingCase={onOpenFinancingCase} initiativeName={d.initiative_name} />
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground" data-testid="sf-fm-disclaimer">{res.disclaimer}</p>
        </>
      )}
    </div>
  );
}
